<template lang="pug">
.table
  .section-title {{ data.label }}
  .table-header-wrapper
    table
      thead
        tr
          th(
            v-for="column in columns",
            :key="column.name",
            :style="column.column_style",
            :class="getColumnHeaderClass(column)"
          )
            .column-header(@click="setSort(column)")
              span.column-header-sorting(v-if="column['sortable']")
                inline-svg.column-sorting-icon(:src="getSortIconSrc(column.name)")
              span.column-header-title.noselect
                | {{ column.label }}
  .table-body-wrapper
    table(
      v-infinite-scroll="loadMore",
      :infinite-scroll-disabled="loading",
      infinite-scroll-distance="300",
      infinite-scroll-immediate-check="false"
    )
      tbody
        template(v-if="tableData.length > 0")
          tr(v-for="item in tableData", :key="item.id")
            td(v-for="column in columns", :key="`${1}_${column.name}`", :style="column.column_style")
              .table-ceil
                sync-message(v-if="column.name === 'sync_message'", :value="item[column.name]")
                .table-ceil-text(v-else) {{ getTableValue(column, item[column.name]) }}
        template(v-else)
          tr
            td.no-result(:colspan="columns.length") {{ notifies.data_not_found }}
        tr(v-if="loading")
          td(:colspan="columns.length")
            q-spinner.loader(color="primary", size="3em")
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { handleError } from "@/services/handleErrors";
import backend from "@/api";
import { notifies } from "@/services/useLocales";
import fields from "../../../general_components/fields";
import { format } from "date-fns";
import syncMessage from "../../table/syncMessage.vue";

const props = defineProps({
  /** Дата текущей секции. Пример: 
  {
    "type": "table",
    "data_url": "/acts/24/table",
    "columns": [
      {
        "key": "created_at",
        "label": "Дата"
      },
      {
        "key": "user",
        "label": "Пользователь"
      },
      {
        "key": "action",
        "label": "Действие"
      }
    ]
  }
    */
  data: { type: Object, default: () => {} },
});

const tableData = ref([]);
const sortBy = ref("");
const sortDirection = ref("");
const page = ref(1);
const loading = ref(true);
const hasNextPage = ref(false);

const data = computed(() => props.data);
const columns = computed(() => data.value.columns ?? []);
const pagination = computed(() => data.value.options?.pagination ?? true);
const path = computed(() => `/api/v3/${data.value.data_url}`);

const getSortIconSrc = name => {
  if (sortBy.value === name) {
    return sortDirection.value === "asc"
      ? require("../../../../../assets/icons/grid/up_arrow.svg")
      : require("../../../../../assets/icons/grid/down_arrow.svg");
  } else {
    return require("../../../../../assets/icons/grid/down_arrow.svg");
  }
};

const resetData = async () => {
  page.value = 1;
  tableData.value = [];
  loading.value = true;

  await nextTick();
  await getTableData();
};

const setSort = column => {
  if (column.sortable) {
    const name = column.name;

    if (sortBy.value === name) {
      sortDirection.value === "asc" ? (sortDirection.value = "desc") : (sortDirection.value = "asc");
      resetData();
    } else {
      sortBy.value = name;
      sortDirection.value = "asc";
      resetData();
    }
  }
};

const getColumnHeaderClass = columns => {
  const classes = [];

  if (columns.classes) classes.push(columns.classes);

  if (columns.sortable) classes.push("pointer");

  return classes.join(" ");
};

const loadMore = async () => {
  if (!loading.value && hasNextPage.value) {
    loading.value = true;
    getTableData();
  }
};

const getTableData = async () => {
  try {
    let params = {};

    if (pagination.value) {
      params.page = page.value;
    }

    if (sortBy.value) {
      params["sort_by"] = sortBy.value;
      params["sort_dir"] = sortDirection.value;
    }

    const { data } = await backend.index(path.value, { params });

    tableData.value = [...tableData.value, ...data.data];

    if (pagination.value) {
      hasNextPage.value = data.pagination.total - data.pagination.per_page * page.value > 0;
      page.value++;
    }
  } catch (err) {
    await handleError(err);
  } finally {
    loading.value = false;
  }
};

const getTableValue = (column, val) => {
  const date = new Date(val);

  if (!val) return "";

  if (column.type === "datetime") {
    return format(date, "dd.MM.yyyy HH:mm");
  } else if (column.type === "date") {
    return format(date, "dd.MM.yyyy");
  }

  return val;
};

onMounted(async () => {
  await getTableData();
});
</script>

<style scoped lang="scss">
.table {
  .table-header-wrapper {
    margin-top: 10px;
  }

  .table-body-wrapper {
    border-bottom: var(--tabs-block-td-border);
    border-right: var(--tabs-block-td-border);
    border-left: var(--tabs-block-td-border);
    max-height: 500px;
    overflow-y: auto;
  }

  .section-title {
    font-size: 16px;
    margin-top: 16px;
    font-weight: 600;
    color: var(--header-title-color);
  }

  table {
    border-spacing: 0;
    margin-bottom: 0;
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;

    thead {
      tr {
        background: linear-gradient(180deg, #5b7aff 0%, #5b7aff 100%), #f2f4ff;
        height: 40px;

        th {
          padding: 10px;
          border: 1px solid #5b7aff;

          &.pointer {
            cursor: pointer;
          }

          .column-header {
            display: flex;
            align-items: center;
          }

          span {
            font-style: normal;
            font-weight: bold;
            font-size: 13px;
            line-height: 19px;
            color: #fff;
          }
        }
      }
    }

    tbody {
      td {
        border: var(--tabs-block-td-border);
        color: var(--tab-text-color);
        padding: 10px;
        word-break: break-word;

        &.no-result {
          text-align: center;
        }
      }

      tr:last-child {
        td {
          border-bottom: none;
        }
      }
    }
  }

  .loader {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
